import React, { useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import styles from "./PatientSelector.module.scss";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { useForm } from "react-hook-form";
import { IdleModalContent } from 'features/routing/components/IdleModalContent/IdleModalContent';
import { PatientContainer } from "./PatientContainer";
import { Redirect } from "react-router-dom";
import * as paths from "features/routing/paths";
import { Button } from "components/Button";
import { FullScreenLoader } from "components/FullScreenLoader/FullScreenLoader";
import { SessionExpiredToast } from "features/login/SessionExpiredToast";
import { Layout } from "features/layout";
import { ciamSessionRequest } from "api/ciamSessionRequest";
import { ciamSessionSwitchRequest } from "api/ciamSessionSwitchRequest";
import { patientConfirmRequest } from "api/patientConfirmRequest";
import { patientMatchingRequest } from "api/patientMatchingRequest";
import { PartialMatchVerification } from "api/PartialMatchVerification";
import { ConfirmPatient } from "types/patient";
import {
  isEmail,
  isValidTLDLength,
  acceptableCharacters,
} from "utils/emailValidation";
import {
  CAIMMaskedDateInput,
  CAIMMaskedPhoneInput,
} from "components/MaskedTextInput/MaskedTextInput";
import { maskPhone } from "components/MaskedTextInput/maskPhone";
import { presenceNameValidation } from "utils/presenceNameValidation";
import { isValidDateOfBirth } from "utils/dateOfBirthValidation";
import { CiamTextInput } from "components/TextInput";
import { PatientQuestions } from "./PatientQuestions";

interface PatientSelectorProps {
  patientList: any | null;
  idToken: string | null;
  closeLoader?: () => void;
  openLoader?: () => void;
  isLoadingMore?: boolean;
}

export const PatientSelector: React.FC<PatientSelectorProps> = ({
  patientList,
  idToken,
  closeLoader,
  openLoader,
  isLoadingMore = false,
}) => {
  const renderFullScreenLoader = () => <FullScreenLoader />;

  // Only show loader for user actions after initial load
  const [loading, setLoading] = React.useState(false);

  // Always use a safe fallback function
  const safeOpenLoader = typeof openLoader === "function" ? openLoader : () => {};
  const safeCloseLoader =
    typeof closeLoader === "function" ? closeLoader : () => {};

  const wrappedOpenLoader = React.useCallback(() => {
    setLoading(true);
    safeOpenLoader();
  }, [safeOpenLoader]);

  const wrappedCloseLoader = React.useCallback(() => {
    setLoading(false);
    safeCloseLoader();
  }, [safeCloseLoader]);

  // -------------------------------------------------------
  // Auto-select / questionnaire shortcuts (no UI flicker)
  // -------------------------------------------------------
  const isSinglePatient =
    Array.isArray(patientList) &&
    patientList.length === 1 &&
    !!patientList[0] &&
    !!idToken;

  const singlePatient = isSinglePatient ? patientList[0] : null;

  const isMaskedSingle =
    singlePatient?.masked === true ||
    singlePatient?.masked === "true" ||
    singlePatient?.masked === 1 ||
    singlePatient?.masked === "1";

  const isSinglePartialMatch = singlePatient?.MatchType === "PM";

  const shouldShowQuestionnaireForSingle =
    isSinglePatient && !isSinglePartialMatch && isMaskedSingle;

  const shouldAutoSelect =
    isSinglePatient && !isSinglePartialMatch && !shouldShowQuestionnaireForSingle;

  const [autoSelectHandled, setAutoSelectHandled] = React.useState(false);

  // Idle/session timeout logic
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(300); // 5 min warning
  const idleTimerRef = React.useRef<any>(null);
  const countdownIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const refreshTimeoutRef = React.useRef<number | null>(null);

  // 5 min idle, 5 min warning (production)
  const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 min
  const WARNING_DURATION = 300; // 5 min (seconds)

  const handleLogoutRedirect = () => {
    window.location.href = paths.portalCaimHome();
  };

  useIdleTimer({
    ref: idleTimerRef,
    timeout: IDLE_TIMEOUT,
    onIdle: () => {
      setShowIdleModal(true);
      setIdleCountdown(WARNING_DURATION);

      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

      countdownIntervalRef.current = setInterval(() => {
        setIdleCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current!);
            setShowIdleModal(false);
            handleLogoutRedirect();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    onActive: () => {
      // Only dismiss modal via buttons, not activity
      if (!showIdleModal) {
        setShowIdleModal(false);
        setIdleCountdown(WARNING_DURATION);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      }
    },
    debounce: 500,
  });

  React.useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  // Form + state
  const formMethods = useForm<ConfirmPatient>({ mode: "onBlur" });
  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    setValue,
  } = formMethods;

  const dummyPatientDetails = {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    dateOfBirth: "",
  };

  const [showForm, setShowForm] = useState(false);
  const [isLoggedIn, setLoggedIn] = React.useState<boolean>(false);
  const [singlePmChecked, setSinglePmChecked] = React.useState(false);
  const [idTokenChanges, setidTokenChanges] = React.useState<string | null>(null);
  const [currentPtChange, setcurrentPtChange] = React.useState<any>(null);
  const [isSubmitLogin, setIsSubmitLogin] = React.useState<boolean>(false);
  const [formError, setFormError] = React.useState<any>(false);
  const [currentPatientList, setcurrentPatientList] = React.useState<any>(null);
  const [submitCount, setSubmitCount] = React.useState<number>(0);
  const [submitLocked, setSubmitLocked] = React.useState(false);
  const [showQuestions, setshowQuestions] = React.useState(false);
  const [isVerified, setIsVerified] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);
  const [patientDetails, setPatientDetails] = React.useState<any>(dummyPatientDetails);
  const [ptIndex, setPTIndex] = React.useState<number>(0);
  const [isRefreshingPatients, setIsRefreshingPatients] = React.useState(false);
  const [refreshSlow, setRefreshSlow] = React.useState(false);

  const firstName = register(
    "firstName",
    presenceNameValidation("Not a valid first name")
  );
  const lastName = register(
    "lastName",
    presenceNameValidation("Not a valid last name")
  );
  const phoneNumber = register("phoneNumber");
  const dateOfBirth = register("dateOfBirth", { validate: isValidDateOfBirth });
  const email = register("email", {
    required: "Not a valid email",
    validate: {
      isEmail: (value) => isEmail(value),
      isValidTLDLength: (value) => isValidTLDLength(value),
      acceptableCharacters: (value) => acceptableCharacters(value),
    },
  });

  let tempPatientList = currentPatientList ? currentPatientList : patientList;

  // ---------------------------------------------
  // Auto-select the only patient (no selector UI)
  // ---------------------------------------------
  React.useEffect(() => {
    if (!shouldAutoSelect) return;
    if (autoSelectHandled) return;

    setAutoSelectHandled(true);

    // critical: unblocks render gates immediately
    setIsReady(true);

    // acts like clicking SELECT
    wrappedOpenLoader();
    setcurrentPtChange(patientList![0]);
    setidTokenChanges(idToken!);
  }, [shouldAutoSelect, autoSelectHandled, patientList, idToken, wrappedOpenLoader]);

  // ------------------------------------------------
  // Single patient masked=true -> show validation form
  // ------------------------------------------------
  React.useEffect(() => {
    if (!shouldShowQuestionnaireForSingle) return;

    setAutoSelectHandled(true);
    setcurrentPtChange(patientList![0]);
    setPTIndex(0);
    setShowForm(true);
    setIsReady(true);
  }, [shouldShowQuestionnaireForSingle, patientList]);

  // ------------------------------------------------
  // Single patient PM -> check verification/lockout
  // ------------------------------------------------
  React.useEffect(() => {
    if (!isSinglePartialMatch || !singlePatient || !idToken) return;

    let mounted = true;

    const checkSinglePatientStatus = async () => {
      wrappedOpenLoader();
      try {
        const patientVerification = await PartialMatchVerification({
          ciam_token: idToken,
          fname: singlePatient.FirstName,
          lname: singlePatient.LastName,
        });

        const status =
          patientVerification?.patients?.validationFormStatuses?.[0] ?? null;

        if (!mounted) return;

        if (status?.isVerified === true) {
          setIsVerified(true);
          setcurrentPtChange(singlePatient);
          setidTokenChanges(idToken);
          setIsReady(true);
          setSinglePmChecked(true);
          return;
        }

        const lockedOut =
          status?.lastLockedOutDate &&
          dayjs(status.lastLockedOutDate).isValid() &&
          dayjs(status.lastLockedOutDate).add(8, "day").isAfter(dayjs());

        if (lockedOut) {
          setIsVerified(true);
          setSubmitCount(3);
          setcurrentPtChange(singlePatient);
          setshowQuestions(true);
          setIsReady(true);
          setSinglePmChecked(true);
          return;
        }

        // Not verified yet (or no status) -> show questionnaire
        setIsVerified(false);
        setSubmitCount(status?.attempts || 0);
        setcurrentPtChange(singlePatient);
        setshowQuestions(true);
        setIsReady(true);
        setSinglePmChecked(true);
      } catch (e) {
        if (!mounted) return;
        // If status check fails, fall back to showing questionnaire
        setIsVerified(false);
        setSubmitCount(0);
        setcurrentPtChange(singlePatient);
        setshowQuestions(true);
        setIsReady(true);
        setSinglePmChecked(true);
      } finally {
        wrappedCloseLoader();
      }
    };

    checkSinglePatientStatus();

    return () => {
      mounted = false;
    };
  }, [isSinglePartialMatch, singlePatient, idToken, wrappedOpenLoader, wrappedCloseLoader]);

  // No patients
  if (patientList && patientList.length === 0 && idToken) {
    safeCloseLoader();
    return <Redirect to={paths.portalCaimHome() + "/?idToken=" + idToken} />;
  }

  // Partial-match verification (multi-patient / PM rules)
  React.useEffect(() => {
    if (!tempPatientList) return;

    const getQuestionVerification = async (patient: any, token: string) => {
      wrappedOpenLoader();
      try {
        const patientVerification = await PartialMatchVerification({
          ciam_token: token,
          fname: patient.FirstName,
          lname: patient.LastName,
        });

        let showFormLocal = true;

        if (
          patientVerification?.patients?.validationFormStatuses?.[0]?.isVerified
        ) {
          setIsReady(true);
        }

        if (patientVerification?.patients?.validationFormStatuses?.length === 0) {
          setcurrentPtChange(patient);
          setshowQuestions(true);
          setIsReady(true);
        }

        const status = patientVerification?.patients?.validationFormStatuses?.[0];
        if (status && !status.isVerified) {
          if (
            status.lastLockedOutDate &&
            dayjs(status.lastLockedOutDate).isValid() &&
            dayjs(status.lastLockedOutDate).add(8, "day").isAfter(dayjs())
          ) {
            setIsVerified(true);
            setSubmitCount(3);
            setcurrentPtChange(patient);
            setshowQuestions(true);
            setIsReady(true);
            showFormLocal = false;
            return;
          }

          if (status.attempts) {
            if (!showFormLocal && status.attempts === 3) {
              setSubmitCount(0);
            } else if (status.attempts === 3) {
              setSubmitCount(status.attempts);
              setIsVerified(true);
            } else {
              setSubmitCount(status.attempts);
            }
          }

          setcurrentPtChange(patient);
          setshowQuestions(true);
          setIsReady(true);
        }
      } catch (e) {
        console.log(e);
        setcurrentPtChange(patient);
        setshowQuestions(true);
        setIsReady(true);
      } finally {
        wrappedCloseLoader();
      }
    };

    // IMPORTANT: don’t run this logic when we’re in the single-patient shortcut paths
    if (shouldAutoSelect || shouldShowQuestionnaireForSingle || isSinglePartialMatch) return;

    let isReadyCheck = false;
    for (let i = 0; i < tempPatientList.length; i++) {
      if (
        tempPatientList[i]["MatchType"] === "PM" &&
        tempPatientList[i]["AccountType"] === "Main" &&
        !tempPatientList[i]["last_exam_date_absent"]
      ) {
        getQuestionVerification(tempPatientList[i], idToken as string);
        isReadyCheck = true;
      }
    }
    if (!isReadyCheck) {
      setIsReady(true);
    }
  }, [
    tempPatientList,
    idToken,
    wrappedOpenLoader,
    wrappedCloseLoader,
    shouldAutoSelect,
    shouldShowQuestionnaireForSingle,
    isSinglePartialMatch,
  ]);

  // Session switch (this is the “Select” behavior)
  React.useEffect(() => {
    if (!idTokenChanges && !currentPtChange) return;
    if (isSubmitLogin) return;

    const getPatientsDetail = async () => {
      wrappedOpenLoader();
      try {
        if (currentPtChange && idTokenChanges) {
          const patientDetailsLocal = currentPtChange;

          await ciamSessionRequest({ id_token: idTokenChanges });

          const phone =
            !patientDetailsLocal.Phones ||
            typeof patientDetailsLocal.Phones[0] !== "object"
              ? ""
              : patientDetailsLocal.Phones[0]["PhoneNumber"];

          await ciamSessionSwitchRequest({
            patient_id: patientDetailsLocal.PatientId,
            first_name: patientDetailsLocal.FirstName,
            last_name: patientDetailsLocal.LastName,
            email: patientDetailsLocal.Email,
            phone_number: phone,
            date_of_birth: patientDetailsLocal.Dob.split(" ")[0],
          });

          setLoggedIn(true);
          setIsSubmitLogin(true);
        }
      } catch (e) {
        setFormError("A system error occurred. Please try again or contact support.");
        setShowForm(false);
        setcurrentPtChange(null);
        setIsSubmitLogin(false);
        console.error("Error in getPatientsDetail:", e);
      } finally {
        wrappedCloseLoader();
      }
    };

    getPatientsDetail();
  }, [currentPtChange, idTokenChanges, isSubmitLogin, wrappedOpenLoader, wrappedCloseLoader]);

  const showConfirmForm = (mask: boolean, patient: any, token: string, ptIndex: number) => {
    if (!mask && patient) {
      if (patient && token) {
        setcurrentPtChange(patient);
        setidTokenChanges(token);
      }
    } else {
      setShowForm(true);
      setcurrentPtChange(patient);
      setPTIndex(ptIndex);
    }
    wrappedOpenLoader();
  };

  const resetConfirmFormState = () => {
    setPatientDetails(dummyPatientDetails);
    setValue("firstName", "");
    setValue("lastName", "");
    setValue("dateOfBirth", "");
    setValue("phoneNumber", "");
    setValue("email", "");
    setFormError(false);
    setcurrentPtChange(null);
  };

  const hideConfirmForm = () => {
    resetConfirmFormState();
    setShowForm(false);
    wrappedCloseLoader();
  };

  const hideQuestionForm = (verified?: boolean) => {
    setShowForm(false);
    setFormError(false);
    setSubmitLocked(false);
    setshowQuestions(false);
    if (verified) {
      const isSingleList = Array.isArray(patientList) && patientList.length === 1;
      const nextPatient =
        isSingleList ? currentPtChange ?? patientList?.[0] ?? null : null;
      if (nextPatient && idToken) {
        setcurrentPtChange(nextPatient);
        setidTokenChanges(idToken);
      }
    }
    wrappedCloseLoader();
  };

  const updatePatient = (fieldValue: any) => {
    const pDetails = cloneDeep(patientDetails);

    if (fieldValue?.field === "firstName") pDetails.firstName = fieldValue?.value;
    if (fieldValue?.field === "lastName") pDetails.lastName = fieldValue?.value;
    if (fieldValue?.field === "phoneNumber") pDetails.phoneNumber = fieldValue?.value;
    if (fieldValue?.field === "email") pDetails.email = fieldValue?.value;
    if (fieldValue?.field === "dateOfBirth") pDetails.dateOfBirth = fieldValue?.value;

    setPatientDetails(pDetails);
    if (formError) setFormError(false);
  };

  React.useEffect(() => {
    if (submitCount >= 3) setShowForm(false);
  }, [submitCount]);

  // -------------------------------------------------------
  // Render gates to prevent selector flicker on single patient
  // -------------------------------------------------------
  if (shouldAutoSelect && !autoSelectHandled) return renderFullScreenLoader();
  if (shouldShowQuestionnaireForSingle && !showForm && !isLoggedIn)
    return renderFullScreenLoader();
  if (isSinglePartialMatch && !singlePmChecked && !isLoggedIn)
    return renderFullScreenLoader();
  if (isSinglePartialMatch && currentPtChange && idTokenChanges && !isLoggedIn)
    return renderFullScreenLoader();
  if (shouldAutoSelect && !isLoggedIn && !formError) return renderFullScreenLoader();

  if (!patientList || !isReady) {
    if (formError) {
      return (
        <Layout showLogout={true}>
          <div className={styles.cardContainer}>
            <h1 className={styles.heading}>Error</h1>
            <p className={styles.errorGlobal}>{formError}</p>
          </div>
        </Layout>
      );
    }
    return null;
  }

  if (isLoggedIn) {
    //return <Redirect to={paths.portalHomeSelector()} />;
    window.location.href = window.origin + "/my-account/home";
    return false;
  }

  const onSubmitNew = async (_data: ConfirmPatient) => {
    if (
      patientDetails.firstName &&
      patientDetails.lastName &&
      patientDetails.dateOfBirth &&
      patientDetails.phoneNumber &&
      patientDetails.email &&
      currentPtChange?.PatientUid &&
      currentPtChange?.PatientId
    ) {
      wrappedOpenLoader();
      let refreshStarted = false;
      try {
        setSubmitLocked(true);

        const confirmPatient = await patientConfirmRequest({
          email: patientDetails.email,
          fname: patientDetails.firstName,
          lname: patientDetails.lastName,
          dob: patientDetails.dateOfBirth,
          phone: patientDetails.phoneNumber,
          patient_uid: currentPtChange.PatientUid,
          patient_id: currentPtChange.PatientId,
          account_type: currentPtChange.AccountType,
          ciam_token: idToken,
        });

        if (confirmPatient && confirmPatient.matched_patient) {
          resetConfirmFormState();
          setShowForm(false);

          wrappedCloseLoader();
          setSubmitLocked(false);
          refreshStarted = true;

          //setIsRefreshingPatients(true);
          //setRefreshSlow(false);
          if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
          /*refreshTimeoutRef.current = window.setTimeout(() => {
            setRefreshSlow(true);
          }, 30000);*/

          try {
            /*
            const patientMatchArr = await patientMatchingRequest({
              ciam_token: idToken,
            });

            setcurrentPatientList(patientMatchArr.matched_patient); */
            tempPatientList[ptIndex]["Dob"] = patientDetails.dateOfBirth;
            tempPatientList[ptIndex]["Email"] = patientDetails.email;
            tempPatientList[ptIndex]["FirstName"] = patientDetails.firstName;
            tempPatientList[ptIndex]["LastName"] = patientDetails.lastName;
            tempPatientList[ptIndex]["Phones"][0]["PhoneNumber"] = patientDetails.phoneNumber;
            tempPatientList[ptIndex]["masked"] = false;
            setcurrentPatientList(tempPatientList);
            setFormError(false);
            setSubmitCount(0);

            const isSingleList =
              Array.isArray(patientList) && patientList.length === 1;
            if (isSingleList && idToken) {
              // Single patient: log in directly after successful validation
              setcurrentPtChange(tempPatientList[ptIndex]);
              setidTokenChanges(idToken);
            } else {
              setcurrentPtChange(null);
            }
          } catch (e) {
            setFormError(true);
            throw e;
          } finally {
            if (refreshTimeoutRef.current) {
              window.clearTimeout(refreshTimeoutRef.current);
              refreshTimeoutRef.current = null;
            }
            setIsRefreshingPatients(false);
            setRefreshSlow(false);
          }
        } else {
          setSubmitCount((c) => c + 1);
          setFormError(true);
        }
      } catch (e) {
        setFormError(true);
        throw e;
      } finally {
        if (!refreshStarted) {
          setSubmitLocked(false);
          wrappedCloseLoader();
        }
      }
    }
  };

  if (showQuestions && isReady) {
    safeCloseLoader();
    return (
      <Layout showLogout={true}>
        <PatientQuestions
          patient={currentPtChange}
          idToken={idToken}
          hideQuestionForm={hideQuestionForm}
          isVerified={isVerified}
          submitCountParent={submitCount}
        />
      </Layout>
    );
  }

  return (
    <>
      {/* Loader only for user actions, not initial mount */}
      {loading && <FullScreenLoader />}

      <Layout showLogout={true}>
        {/* Overlay modal div that blocks all content when idle */}
        {showIdleModal && (
          <div className={styles.idleModalOverlay}>
            <div
              className={styles.idleModalContentWrapper}
              tabIndex={0}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <button
                className={styles.idleModalCloseButton}
                aria-label="Close"
                onClick={() => {
                  setShowIdleModal(false);
                  setIdleCountdown(WARNING_DURATION);
                  if (idleTimerRef.current && typeof idleTimerRef.current.reset === "function") {
                    idleTimerRef.current.reset();
                  }
                  if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
                }}
                type="button"
              >
                <span className={styles.idleModalCloseLabel}>Close</span>
                <span className={styles.idleModalCloseIcon}>&times;</span>
              </button>

              <IdleModalContent
                timer={idleCountdown * 1000}
                dismiss={() => {
                  setShowIdleModal(false);
                  setIdleCountdown(WARNING_DURATION);
                  if (idleTimerRef.current && typeof idleTimerRef.current.reset === "function") {
                    idleTimerRef.current.reset();
                  }
                  if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
                }}
              />
            </div>
          </div>
        )}

        <SessionExpiredToast />

        <div className={!showForm ? styles.showContainer : styles.hideContainer}>
          {isRefreshingPatients && (
            <div className={styles.bannerContainer} role="status" aria-live="polite">
              <p className={styles.bannerHeading}>Refreshing your patient list</p>
              <p className={styles.bannerText}>
                This can take a little while. You can continue using the current list and it will
                update automatically when ready.
              </p>
              {refreshSlow && (
                <p className={styles.bannerText}>
                  Still working on it. If this takes too long, you can continue and refresh later.
                </p>
              )}
            </div>
          )}
          {patientList.length > 0 && (
            <div>
              <div className={styles.cardContainer}>
                {isLoadingMore && (
                  <div className={styles.inlineSpinnerWrapper} aria-live="polite">
                    <div className={styles.inlineSpinner} />
                  </div>
                )}
                {!showQuestions && submitCount < 3 && (
                  <>
                    <div className={styles.patientListContainer}>
                      <div>
                        <h1 className={styles.heading}>
                          WE MAY HAVE FOUND OTHER PATIENTS ASSOCIATED WITH YOUR ACCOUNT
                        </h1>
                        <p className={styles.info}>
                          We prioritize patient data security. If you are seeing partial patient
                          information below, please select one of the patients to verify a few
                          additional details and receive access to their account information.
                        </p>
                      </div>
                    </div>

                    <div className={styles.divider}></div>

                    <div className={styles.patientList}>
                      {tempPatientList.map((patient: any, i: number) => (
                        <PatientContainer
                          key={`${patient?.PatientId ?? "pt"}-${i}`}
                          patient={patient}
                          index={i}
                          masked={patient.masked}
                          showConfirmForm={showConfirmForm}
                          idToken={idToken}
                          calledFrom="form"
                        />
                      ))}
                    </div>
                  </>
                )}

                {!showQuestions && submitCount === 3 && (
                  <>
                    <div className={styles.patientListContainer}>
                      <div>
                        <h1 className={styles.heading}>Associated Patient Linking Failed</h1>
                        <p className={styles.info}>
                          We prioritize patient data security. Unfortunately, after 3 attempts you
                          were unsuccessful in verifying the information about the patient(s) shown
                          below. Often this happens if a name was misspelled in our records, or the
                          contact information for the patient has changed.
                        </p>
                        <p className={styles.info1}>
                          Please{" "}
                          <a
                            href="https://locations.myeyedr.com/search"
                            className={styles.bannerLink}
                          >
                            contact your local MyEyeDr. office
                          </a>{" "}
                          for further assistance.
                        </p>
                      </div>
                    </div>

                    <div className={styles.divider}></div>

                    <div className={styles.patientList}>
                      <PatientContainer
                        patient={currentPtChange}
                        index={1}
                        masked={currentPtChange?.masked}
                        showConfirmForm={showConfirmForm}
                        errorContainer={true}
                        idToken={idToken}
                        calledFrom="error"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className={styles.bannerContainer}>
                <div>
                  <h2 className={styles.bannerHeading}>
                    Need to make changes to your account or personal information?
                  </h2>
                  <p className={styles.bannerText}>
                    If you need to add or update any of the information you see about your MyEyeDr
                    account, please{" "}
                    <a
                      href="https://locations.myeyedr.com/search"
                      className={styles.bannerLink}
                    >
                      contact your nearest MyEyeDr location
                    </a>{" "}
                    for assistance.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {showQuestions && (
          <PatientQuestions
            patient={currentPtChange}
            idToken={idToken}
            hideQuestionForm={hideQuestionForm}
            isVerified={isVerified}
            submitCountParent={submitCount}
          />
        )}

        {currentPtChange && showForm && (
          <div className={showForm ? styles.showContainer : styles.hideContainer}>
            <div className={styles.cardContainer}>
              <div className={styles.ConfirmDataForm}>
                <div>
                  <div className={styles.back} onClick={hideConfirmForm}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                    >
                      <path
                        stroke="var(--blue-00)"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 12H5M12 19l-7-7 7-7"
                      ></path>
                    </svg>
                    <span>Go back</span>
                  </div>
                </div>

                <h1 className={styles.heading}>Confirm patient data</h1>
                <p className={styles.paragraph}>
                  Complete the form below with the details of the masked patient. Please ensure
                  that the provided information matches the details used during the appointment.
                </p>

                <div>
                  <div className={styles.patientdatacontainer}>
                    <div className={styles.patientdatarow}>
                      <div className={styles.patientdatafield}>
                        <span className={styles.labelForm}>Patient</span>
                        <p className={styles.field}>
                          {currentPtChange.FirstName} {currentPtChange.LastName}
                        </p>
                      </div>
                      <div className={styles.patientdatafield}>
                        <span className={styles.labelForm}>Date of Birth</span>
                        <p className={styles.field}>{currentPtChange.Dob}</p>
                      </div>
                      <div className={styles.patientdatafield}>
                        <span className={styles.labelForm}>Phone Number</span>
                        <p className={styles.field}>
                          {!currentPtChange["Phones"] ||
                          typeof currentPtChange["Phones"][0] !== "object"
                            ? ""
                            : currentPtChange["Phones"][0]["PhoneNumber"]}
                        </p>
                      </div>
                      <div className={styles.patientdatafield}>
                        <span className={styles.labelForm}>Email</span>
                        <p className={styles.field}>{currentPtChange.Email}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <form>
                  <CiamTextInput
                    {...firstName}
                    className={styles.textInput}
                    maxLength={20}
                    inputType="text"
                    onChange={(e) => {
                      updatePatient({
                        field: "firstName",
                        value: e.target.value,
                        isFormValid: isValid,
                      });
                      firstName.onChange(e);
                    }}
                    errorMessage={formError || errors.firstName?.message}
                    defaultValue={patientDetails?.firstName}
                  >
                    <label className={styles.labelForm}>First Name*</label>
                  </CiamTextInput>

                  <CiamTextInput
                    {...lastName}
                    className={styles.textInput}
                    maxLength={20}
                    inputType="text"
                    onChange={(e) => {
                      updatePatient({
                        field: "lastName",
                        value: e.target.value,
                        isFormValid: isValid,
                      });
                      lastName.onChange(e);
                    }}
                    errorMessage={formError || errors.lastName?.message}
                    defaultValue={patientDetails?.lastName}
                  >
                    <label className={styles.labelForm}>Last Name*</label>
                  </CiamTextInput>

                  <CAIMMaskedDateInput
                    {...dateOfBirth}
                    className={styles.textInput}
                    onChange={(e) => {
                      const parsedDate = dayjs(e.target.value);
                      const datestr = parsedDate.isValid()
                        ? dayjs(e.target.value).format("MM/DD/YYYY")
                        : e.target.value;

                      updatePatient({
                        field: "dateOfBirth",
                        value: datestr,
                        isFormValid: isValid,
                      });

                      dateOfBirth.onChange(e);
                    }}
                    errorMessage={formError || (errors as any).dateOfBirth?.message}
                    setValue={setValue}
                    defaultValue={
                      patientDetails?.dateOfBirth &&
                      dayjs(patientDetails?.dateOfBirth).format("MM/DD/YYYY")
                    }
                  >
                    <label className={styles.labelForm}>Date of Birth*</label>
                  </CAIMMaskedDateInput>

                  <CiamTextInput
                    {...email}
                    className={styles.textInput}
                    inputType="text"
                    onChange={(e) => {
                      updatePatient({
                        field: "email",
                        value: e.target.value,
                        isFormValid: isValid,
                      });
                      email.onChange(e);
                    }}
                    errorMessage={formError || errors.email?.message}
                    defaultValue={patientDetails?.email}
                  >
                    <label className={styles.labelForm}>Email*</label>
                  </CiamTextInput>

                  <CAIMMaskedPhoneInput
                    {...phoneNumber}
                    className={styles.textInput}
                    onChange={(e) => {
                      updatePatient({
                        field: "phoneNumber",
                        value: e.target.value.replace(/\D/g, ""),
                        isFormValid: isValid,
                      });
                      phoneNumber.onChange(e);
                    }}
                    setValue={setValue}
                    errorMessage={formError || (errors as any).phoneNumber?.message}
                    defaultValue={
                      patientDetails?.phoneNumber && maskPhone(patientDetails.phoneNumber)
                    }
                  >
                    <label className={styles.labelForm}>Phone Number*</label>
                  </CAIMMaskedPhoneInput>

                  <div className={styles.infoForm}>
                    By clicking <strong>CONFIRM</strong>, I acknowledge I have the legal authority
                    and/or this patient’s written consent to access their healthcare information
                    and make healthcare decisions on their behalf.
                  </div>

                  <Button
                    text="CONFIRM"
                    onClick={handleSubmit(onSubmitNew)}
                    className={styles.submit}
                    disabled={submitLocked}
                    loading={submitLocked}
                  ></Button>

                  {formError && (
                    <div>
                      <p className={styles.errorGlobal}>
                        The information provided does not match our records. Please verify that the
                        information provided is accurate. If the problem persists, please contact
                        your local{" "}
                        <a href="https://locations.myeyedr.com/search">MyEyeDr. office</a>.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>

            <div className={styles.bannerContainer}>
              <div>
                <h2 className={styles.bannerHeading}>
                  Need to make changes to your account or personal information?
                </h2>
                <p className={styles.bannerText}>
                  If you need to add or update any of the information you see about your MyEyeDr
                  account, please{" "}
                  <a href="https://locations.myeyedr.com/search" className={styles.bannerLink}>
                    contact your nearest MyEyeDr location
                  </a>{" "}
                  for assistance.
                </p>
              </div>
            </div>
          </div>
        )}
      </Layout>
    </>
  );
};
